import { useState, useCallback, useEffect, useRef } from 'react';
import { useFormContext } from '../../../../context/form';
import { CreativityType } from '../../../../types/banner';
import type {
  FormField,
  PolymorphicField,
  PolymorphicSubValue,
  PolymorphicValue,
  RoutedFormData,
  FormDataValue
} from '../../../../types/templates';
import { getCreativityForms } from '../../../../clients/registry';
import { getHandlerForTemplate } from '../../../../handlers';
import { useClientId } from '../../../use-client-id';
import { validateVideoUrl, validateRequiredFields, isVideoUrlField } from '../../../../utils';
import { useValidateMedia } from '../../use-validate-media';
import { useTemplateConfig } from '../../use-template-config';

function isMediaUrl(url: string): boolean {
  return /^https?:\/\//i.test(url) && /\.(mp4|jpg|jpeg|png|webp)(\?|$)/i.test(url);
}

function extractMediaUrl(value: FormDataValue): string | null {
  if (!value) return null;
  if (typeof value === 'string') return isMediaUrl(value) ? value : null;
  if (typeof value !== 'object') return null;

  const v = value as Record<string, unknown>;
  const candidates = [
    v.originalUrl,
    v.url,
    v.src,
    v.href,
    v.value,
    (v.image as Record<string, unknown> | undefined)?.originalUrl,
    (v.image as Record<string, unknown> | undefined)?.url
  ];

  return (candidates.find(c => typeof c === 'string' && isMediaUrl(c as string)) as string | undefined) ?? null;
}

const isFieldFilled = (v: FormDataValue): boolean =>
  v !== undefined &&
  v !== null &&
  (typeof v !== 'string' || v.trim().length > 0) &&
  (!Array.isArray(v) || v.length > 0);

function buildPolymorphicDefault(field: PolymorphicField): PolymorphicValue {
  const variant = field.variants.find(v => v.key === field.defaultVariant);
  const values: Record<string, PolymorphicSubValue> = {};
  variant?.fields.forEach(sub => {
    if ('defaultValue' in sub && sub.defaultValue !== undefined) {
      values[sub.key] = sub.defaultValue;
    }
  });
  return { variant: field.defaultVariant, values };
}

export const useCreativityForm = (creativityType: CreativityType, initialValues?: RoutedFormData) => {
  const { setCreativityType } = useFormContext();
  const { validateMediaUrl } = useValidateMedia();
  const clientId = useClientId();

  const clientHandler = getHandlerForTemplate(creativityType);
  const formConfig = getCreativityForms(clientId)[creativityType];
  const template = useTemplateConfig(creativityType);
  const fields: readonly FormField[] = formConfig?.fields ?? [];
  const allFields: readonly FormField[] = template?.formFields ?? fields;
  const requiredKeys = allFields.filter(f => f.required).map(f => f.key);

  const [formData, setFormData] = useState<RoutedFormData>(() => {
    if (!formConfig) return initialValues ?? {};
    const defaults: RoutedFormData = {};
    (template ? allFields : fields).forEach(field => {
      if (field.type === 'polymorphic') {
        defaults[field.key] = buildPolymorphicDefault(field);
        return;
      }
      if ('defaultValue' in field && field.defaultValue !== undefined) {
        defaults[field.key] = field.defaultValue;
      }
    });
    return initialValues ? { ...defaults, ...initialValues } : defaults;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  useEffect(() => {
    setCreativityType(creativityType);
  }, [creativityType, setCreativityType]);

  const setFieldError = useCallback((fieldKey: string, error?: string) => {
    setErrors(prev => {
      const next = { ...prev };
      if (error && error.trim()) {
        next[fieldKey] = error;
      } else {
        delete next[fieldKey];
      }
      return next;
    });
  }, []);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    const requiredFields = allFields.filter(field => field.required).map(field => field.key);
    const missingFields = validateRequiredFields(formData, requiredFields);

    missingFields.forEach(field => {
      const fieldConfig = allFields.find(f => f.key === field);
      newErrors[field] = `${fieldConfig?.label || 'Este campo'} es requerido`;
    });

    Object.entries(formData).forEach(([key, value]) => {
      if (isVideoUrlField(value)) {
        const validation = validateVideoUrl(value.originalUrl);
        if (!validation.isValid) {
          newErrors[key] = 'Debe ser una URL válida (.mp4) o de Instagram/TikTok';
        }
      }
    });

    if (clientHandler.validateForm) {
      const clientErrors = clientHandler.validateForm({ ...formData, _creativityType: creativityType }, newErrors);
      Object.assign(newErrors, clientErrors);
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, [formData, clientHandler, creativityType, allFields]);

  const handleFieldChange = useCallback(
    async (fieldKey: string, value: FormDataValue): Promise<RoutedFormData> => {
      const next: RoutedFormData = { ...formDataRef.current, [fieldKey]: value };

      if (clientHandler.applyFieldLogic) {
        const updates = clientHandler.applyFieldLogic(fieldKey, value, {
          ...next,
          _creativityType: creativityType
        });
        Object.assign(next, updates);
      }

      setFormData(next);
      setFieldError(fieldKey, undefined);

      const mediaUrl = extractMediaUrl(value);
      if (mediaUrl) {
        try {
          const res = await validateMediaUrl(mediaUrl);
          if (!res?.ok) {
            setFieldError(fieldKey, 'La URL no existe o no es accesible');
          } else {
            setFieldError(fieldKey, undefined);
          }
        } catch {
          console.warn('Media validation timeout — ignored');
        }
      }

      return next;
    },
    [creativityType, clientHandler, setFieldError, validateMediaUrl]
  );

  const isFormComplete = requiredKeys.every(k => isFieldFilled(formData[k])) && Object.keys(errors).length === 0;

  return {
    formConfig,
    formData,
    errors,
    isFormComplete,
    fields,
    handleFieldChange,
    validateForm,
    setFieldError
  };
};
